import React, { useCallback, useMemo, useState } from 'react';
import { Form, Input, Modal, Button, Spin, Tree, Tooltip, Empty } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import { useDispatch } from 'react-redux';
import classNames from 'classnames';
import type { SimpleFolderItemDto } from '@api/Api';
import { useCurrentWorkspace } from '@hooks/workspace';
import {
  toggleCampaignFolderCreationModal,
  toggleMoveModal
} from '@redux/actions/modalAction';
import { useMoveAssetsMutation } from '@hooks/queries/assets';
import { useFolderTreeQueries } from '@hooks/queries/folders';
import { MoveModalStateProps } from '@redux/types/modalType';
import {
  ArrowDownIcon,
  CampaingIcon,
  CrossIcon,
  FolderIcon,
  FolderPrivateIcon,
  ImgSecondaryIcon,
  PlusIcon,
  SearchIcon
} from '@icons';
import './MoveModal.less';

type FormValues = {
  selectedFolderId: string | null;
  searchText: string;
  selectedFolderName: string | null;
  selectedFolderIsPrivate: boolean;
};

export default function MoveModal(props: MoveModalStateProps) {
  const { visible, campaignId, campaignName } = props;
  const assets = props.assets ?? [];
  const folders = props.folders ?? [];
  const itemsCount = assets.length + folders.length;
  const isSingleAsset = assets.length === 1 && !folders.length;
  const isSingleFolder = folders.length === 1 && !assets.length;
  const isOneOfEach = assets.length === 1 && folders.length === 1;
  const showNames = isSingleAsset || isSingleFolder || isOneOfEach;
  const { moveToCampaign, moveToPublicFolder, moveToPrivateFolder } =
    props.permissions;

  const [currentWorkspace] = useCurrentWorkspace(false);
  const dispatch = useDispatch();
  const [form] = Form.useForm<FormValues>();

  const searchText = Form.useWatch('searchText', form) ?? '';
  const selectedFolderId = Form.useWatch('selectedFolderId', form) ?? '';
  const selectedFolderName = Form.useWatch('selectedFolderName', form) ?? '';
  const selectedFolderIsPrivate =
    Form.useWatch('selectedFolderIsPrivate', form) ?? false;

  const [expanded, setExpanded] = useState<
    { campaignId: string; folderId?: string }[]
  >([{ campaignId }]);

  const folderQueries = useFolderTreeQueries(
    { workspaceId: currentWorkspace?.id || '', expanded },
    { enabled: visible && !!currentWorkspace?.id }
  );

  const onCreateFolder = useCallback(
    (parentCampaignId: string, parentId?: string) => {
      if (!parentCampaignId) return;
      dispatch(
        toggleCampaignFolderCreationModal({
          visible: true,
          entity: 'folder',
          campaignId: parentCampaignId,
          parentId
        })
      );
    },
    [dispatch]
  );

  const { rootFolders, isRootFoldersLoading } = useMemo(() => {
    const rootIndex = expanded.findIndex(
      (e) => e.campaignId === campaignId && !e.folderId
    );
    if (rootIndex === -1)
      return { rootFolders: [], isRootFoldersLoading: false };
    const query = folderQueries[rootIndex];
    return {
      rootFolders: query?.data ?? [],
      isRootFoldersLoading: query?.isLoading ?? false
    };
  }, [expanded, campaignId, folderQueries]);

  const filteredRootFolders = useMemo(() => {
    if (!searchText) return rootFolders;
    return rootFolders.filter((f: SimpleFolderItemDto) =>
      f.name.toLocaleLowerCase().includes(searchText.toLocaleLowerCase())
    );
  }, [rootFolders, searchText]);

  const selectionLabel = [
    assets.length
      ? `${assets.length} ${assets.length === 1 ? 'item' : 'items'}`
      : null,
    folders.length
      ? `${folders.length} ${folders.length === 1 ? 'folder' : 'folders'}`
      : null
  ]
    .filter(Boolean)
    .join(', ');

  const treeData = useMemo(() => {
    const folderIds = new Set(folders.map((f) => f.id));

    function getChildren(
      parentFolderId: string
    ): { isLoading: boolean; data: object[] } | undefined {
      const queryIndex = expanded.findIndex(
        (e) => e.campaignId === campaignId && e.folderId === parentFolderId
      );
      if (queryIndex === -1) return undefined;
      const query = folderQueries[queryIndex];
      return {
        isLoading: query.isLoading,
        data:
          query.data?.map((folder: SimpleFolderItemDto) => {
            const self = folderIds.has(folder.id);
            const children = self ? undefined : getChildren(folder.id);
            const permDisabled = folder.isPrivate
              ? !moveToPrivateFolder
              : !moveToPublicFolder;
            return {
              icon: folder.isPrivate ? <FolderPrivateIcon /> : <FolderIcon />,
              key: folder.id,
              folderId: folder.id,
              folderName: folder.name,
              isPrivate: folder.isPrivate,
              isLeaf: self,
              disabled: self || permDisabled,
              switcherIcon: children?.isLoading ? (
                <Spin
                  indicator={<LoadingOutlined style={{ fontSize: 16 }} spin />}
                />
              ) : undefined,
              title: (
                <span className="copy-modal__tree-title">
                  <span className="copy-modal__tree-title-text">
                    {folder.name}
                  </span>
                  {!self && (
                    <Tooltip
                      title="Create subfolder"
                      overlayClassName="toggle_favorite_overlay"
                    >
                      <button
                        type="button"
                        className="copy-modal__create-folder-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          onCreateFolder(campaignId, folder.id);
                        }}
                      >
                        <PlusIcon />
                      </button>
                    </Tooltip>
                  )}
                </span>
              ),
              children: children?.data
            };
          }) ?? []
      };
    }

    const folderNodes = filteredRootFolders.map(
      (folder: SimpleFolderItemDto) => {
        const self = folderIds.has(folder.id);
        const children = self ? undefined : getChildren(folder.id);
        const permDisabled = folder.isPrivate
          ? !moveToPrivateFolder
          : !moveToPublicFolder;
        return {
          icon: folder.isPrivate ? <FolderPrivateIcon /> : <FolderIcon />,
          key: folder.id,
          folderId: folder.id,
          folderName: folder.name,
          isPrivate: folder.isPrivate,
          isLeaf: self,
          disabled: self || permDisabled,
          switcherIcon: children?.isLoading ? (
            <Spin
              indicator={<LoadingOutlined style={{ fontSize: 16 }} spin />}
            />
          ) : undefined,
          title: (
            <span className="copy-modal__tree-title">
              <span className="copy-modal__tree-title-text">{folder.name}</span>
              {!self && (
                <Tooltip
                  title="Create subfolder"
                  overlayClassName="toggle_favorite_overlay"
                >
                  <button
                    type="button"
                    className="copy-modal__create-folder-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      onCreateFolder(campaignId, folder.id);
                    }}
                  >
                    <PlusIcon />
                  </button>
                </Tooltip>
              )}
            </span>
          ),
          children: children?.data
        };
      }
    );

    return [
      {
        key: '__campaign_root__',
        icon: <CampaingIcon />,
        title: (
          <span className="copy-modal__tree-title">
            <span className="copy-modal__tree-title-text">{campaignName}</span>
            <Tooltip
              title="Create folder"
              overlayClassName="toggle_favorite_overlay"
            >
              <button
                type="button"
                className="copy-modal__create-folder-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  onCreateFolder(campaignId);
                }}
              >
                <PlusIcon />
              </button>
            </Tooltip>
          </span>
        ),
        isLeaf: false,
        isCampaignRoot: true,
        disabled: !moveToCampaign,
        switcherIcon: isRootFoldersLoading ? (
          <Spin indicator={<LoadingOutlined style={{ fontSize: 16 }} spin />} />
        ) : undefined,
        children: folderNodes
      }
    ];
  }, [
    filteredRootFolders,
    expanded,
    campaignId,
    campaignName,
    folderQueries,
    folders,
    moveToCampaign,
    moveToPublicFolder,
    moveToPrivateFolder,
    isRootFoldersLoading,
    onCreateFolder
  ]);

  const { mutateAsync: moveAssets, isPending } = useMoveAssetsMutation();

  const onCloseModal = useCallback(() => {
    dispatch(toggleMoveModal(null));
  }, [dispatch]);

  const onSubmit = () => {
    const targetFolderId =
      selectedFolderId === '__campaign_root__' ? null : selectedFolderId;
    const assetId = [...assets.map((a) => a.id), ...folders.map((f) => f.id)];
    if (!assetId.length) return;

    moveAssets(
      {
        assetId,
        folderId: targetFolderId
      },
      { onSuccess: onCloseModal }
    );
  };

  const sourceName = (
    <>
      {showNames &&
        folders.map((folder) => (
          <div className="copy-modal__name" key={folder.id}>
            <FolderIcon />
            <Tooltip
              placement="top"
              overlayClassName="toggle_favorite_overlay"
              title={folder.name}
            >
              <div className="copy-modal__name-text">{folder.name}</div>
            </Tooltip>
          </div>
        ))}
      {showNames &&
        assets.map((asset) => (
          <div className="copy-modal__name" key={asset.id}>
            <ImgSecondaryIcon />
            <Tooltip
              placement="top"
              overlayClassName="toggle_favorite_overlay"
              title={asset.name}
            >
              <div className="copy-modal__name-text">{asset.name}</div>
            </Tooltip>
          </div>
        ))}
      {!showNames && (
        <div className="copy-modal__name">
          <ImgSecondaryIcon />
          <div className="copy-modal__name-text">{selectionLabel}</div>
        </div>
      )}
    </>
  );

  return (
    <Modal
      open={visible}
      footer={null}
      centered={true}
      width={656}
      destroyOnClose
      zIndex={1000}
      closeIcon={<CrossIcon />}
      bodyStyle={{ maxHeight: 'calc(100vh - 30px)', height: 'auto' }}
      onCancel={onCloseModal}
      className={classNames('copy-modal')}
    >
      <Form
        form={form}
        onFinish={onSubmit}
        validateTrigger="onChange"
        layout="vertical"
      >
        <h2
          className="modal_container__title"
          style={{ fontSize: 22, marginBottom: 8 }}
        >
          Move to
        </h2>
        {!selectedFolderId && (
          <div className="copy-modal__top">
            Select a destination to move
            {sourceName}
          </div>
        )}
        {!!selectedFolderId && (
          <div className="copy-modal__top">
            Move
            {sourceName}
            into
            <div className="copy-modal__name">
              {selectedFolderId === '__campaign_root__' ? (
                <>
                  <CampaingIcon />
                  <Tooltip
                    placement="top"
                    overlayClassName="toggle_favorite_overlay"
                    title={campaignName}
                  >
                    <div className="copy-modal__name-text">{campaignName}</div>
                  </Tooltip>
                </>
              ) : (
                <>
                  {selectedFolderIsPrivate ? (
                    <FolderPrivateIcon />
                  ) : (
                    <FolderIcon />
                  )}
                  <Tooltip
                    placement="top"
                    overlayClassName="toggle_favorite_overlay"
                    title={selectedFolderName}
                  >
                    <div className="copy-modal__name-text">
                      {selectedFolderName}
                    </div>
                  </Tooltip>
                </>
              )}
            </div>
          </div>
        )}
        <Form.Item className="copy-modal__search-input">
          <Form.Item name="searchText" noStyle>
            <Input
              placeholder="Search by folder"
              prefix={<SearchIcon />}
              size="large"
            />
          </Form.Item>
        </Form.Item>
        <div className="copy-modal__tree-wrapper">
          {treeData.length === 0 ? (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="Nothing found"
            />
          ) : (
            <Tree
              treeData={treeData}
              showIcon
              showLine
              selectedKeys={selectedFolderId ? [selectedFolderId] : []}
              expandedKeys={[
                '__campaign_root__',
                ...expanded
                  .filter(
                    (x): x is { campaignId: string; folderId: string } =>
                      !!x.folderId
                  )
                  .map((x) => x.folderId)
              ]}
              onExpand={(_keys, { expanded: isExpanding, node }) => {
                const n = node as any;
                if (n.isCampaignRoot) return;
                if (isExpanding) {
                  setExpanded((prev) => [
                    ...prev,
                    { campaignId, folderId: n.folderId }
                  ]);
                } else {
                  setExpanded((prev) =>
                    prev.filter(
                      (e) =>
                        !(
                          e.campaignId === campaignId &&
                          e.folderId === n.folderId
                        )
                    )
                  );
                }
              }}
              onSelect={(_selectedKeys, { node }) => {
                const n = node as any;
                if (n.disabled) return;
                if (n.selected) {
                  form.setFieldsValue({
                    selectedFolderId: '',
                    selectedFolderName: null,
                    selectedFolderIsPrivate: false
                  });
                  return;
                }
                form.setFieldsValue({
                  selectedFolderId: n.key,
                  selectedFolderName: n.isCampaignRoot
                    ? null
                    : (n.folderName as string),
                  selectedFolderIsPrivate: n.isCampaignRoot
                    ? false
                    : (n.isPrivate as boolean)
                });
              }}
              switcherIcon={<ArrowDownIcon />}
            />
          )}
        </div>
        <Form.Item name="selectedFolderId" hidden>
          <Input size="large" />
        </Form.Item>
        <Form.Item name="selectedFolderName" hidden />
        <Form.Item name="selectedFolderIsPrivate" hidden />
        <div className="modal_container__actions">
          <Button
            type="text"
            onClick={onCloseModal}
            style={{
              marginRight: 12,
              border: 'none',
              height: 48,
              padding: '12px 19px'
            }}
          >
            Cancel
          </Button>
          <Button
            type="primary"
            size="large"
            loading={isPending}
            disabled={!selectedFolderId}
            htmlType="submit"
            style={{ width: 152, height: 48, padding: '8px 12px' }}
          >
            {isSingleFolder
              ? 'Move folder'
              : `Move ${itemsCount} item${itemsCount > 1 ? 's' : ''}`}
          </Button>
        </div>
      </Form>
    </Modal>
  );
}
